"use client";

import { c as _c } from "react/compiler-runtime";
import { X as CloseX } from "@untitledui/icons";
import type { MouseEventHandler, ReactNode } from "react";
import { Dot } from "@/components/foundations/dot-icon";
import { IMAGE_OUTLINE_CLASS } from "@/consts/typography";
import { cx } from "@/utils/cx";
import type { BadgeColors, BadgeTypeToColorMap, BadgeTypes, FlagTypes, IconComponentType, Sizes } from "./badge-types";
import { badgeTypes } from "./badge-types";
export const filledColors: Record<BadgeColors, {
  root: string;
  addon: string;
  addonButton: string;
}> = {
  gray: {
    root: "bg-utility-gray-50 text-utility-gray-700 ring-utility-gray-200",
    addon: "text-utility-gray-500",
    addonButton: "hover:bg-utility-gray-100 text-utility-gray-400 hover:text-utility-gray-500"
  },
  brand: {
    root: "bg-utility-brand-50 text-utility-brand-700 ring-utility-brand-200",
    addon: "text-utility-brand-500",
    addonButton: "hover:bg-utility-brand-100 text-utility-brand-400 hover:text-utility-brand-500"
  },
  error: {
    root: "bg-utility-error-50 text-utility-error-700 ring-utility-error-200",
    addon: "text-utility-error-500",
    addonButton: "hover:bg-utility-error-100 text-utility-error-400 hover:text-utility-error-500"
  },
  warning: {
    root: "bg-utility-warning-50 text-utility-warning-700 ring-utility-warning-200",
    addon: "text-utility-warning-500",
    addonButton: "hover:bg-utility-warning-100 text-utility-warning-400 hover:text-utility-warning-500"
  },
  success: {
    root: "bg-utility-success-50 text-utility-success-700 ring-utility-success-200",
    addon: "text-utility-success-500",
    addonButton: "hover:bg-utility-success-100 text-utility-success-400 hover:text-utility-success-500"
  },
  "gray-blue": {
    root: "bg-utility-gray-blue-50 text-utility-gray-blue-700 ring-utility-gray-blue-200",
    addon: "text-utility-gray-blue-500",
    addonButton: "hover:bg-utility-gray-blue-100 text-utility-gray-blue-400 hover:text-utility-gray-blue-500"
  },
  "blue-light": {
    root: "bg-utility-blue-light-50 text-utility-blue-light-700 ring-utility-blue-light-200",
    addon: "text-utility-blue-light-500",
    addonButton: "hover:bg-utility-blue-light-100 text-utility-blue-light-400 hover:text-utility-blue-light-500"
  },
  blue: {
    root: "bg-utility-blue-50 text-utility-blue-700 ring-utility-blue-200",
    addon: "text-utility-blue-500",
    addonButton: "hover:bg-utility-blue-100 text-utility-blue-400 hover:text-utility-blue-500"
  },
  indigo: {
    root: "bg-utility-indigo-50 text-utility-indigo-700 ring-utility-indigo-200",
    addon: "text-utility-indigo-500",
    addonButton: "hover:bg-utility-indigo-100 text-utility-indigo-400 hover:text-utility-indigo-500"
  },
  purple: {
    root: "bg-utility-purple-50 text-utility-purple-700 ring-utility-purple-200",
    addon: "text-utility-purple-500",
    addonButton: "hover:bg-utility-purple-100 text-utility-purple-400 hover:text-utility-purple-500"
  },
  pink: {
    root: "bg-utility-pink-50 text-utility-pink-700 ring-utility-pink-200",
    addon: "text-utility-pink-500",
    addonButton: "hover:bg-utility-pink-100 text-utility-pink-400 hover:text-utility-pink-500"
  },
  orange: {
    root: "bg-utility-orange-50 text-utility-orange-700 ring-utility-orange-200",
    addon: "text-utility-orange-500",
    addonButton: "hover:bg-utility-orange-100 text-utility-orange-400 hover:text-utility-orange-500"
  },
  "network-ethereum": {
    root: "bg-utility-network-ethereum-50 text-utility-network-ethereum-700 ring-utility-network-ethereum-200",
    addon: "text-utility-network-ethereum-500",
    addonButton: "hover:bg-utility-network-ethereum-100 text-utility-network-ethereum-400 hover:text-utility-network-ethereum-500"
  },
  "network-arbitrum": {
    root: "bg-utility-network-arbitrum-50 text-utility-network-arbitrum-700 ring-utility-network-arbitrum-200",
    addon: "text-utility-network-arbitrum-500",
    addonButton: "hover:bg-utility-network-arbitrum-100 text-utility-network-arbitrum-400 hover:text-utility-network-arbitrum-500"
  },
  "network-base": {
    root: "bg-utility-network-base-50 text-utility-network-base-700 ring-utility-network-base-200",
    addon: "text-utility-network-base-500",
    addonButton: "hover:bg-utility-network-base-100 text-utility-network-base-400 hover:text-utility-network-base-500"
  },
  "network-optimism": {
    root: "bg-utility-network-optimism-50 text-utility-network-optimism-700 ring-utility-network-optimism-200",
    addon: "text-utility-network-optimism-500",
    addonButton: "hover:bg-utility-network-optimism-100 text-utility-network-optimism-400 hover:text-utility-network-optimism-500"
  },
  "network-unichain": {
    root: "bg-utility-network-unichain-50 text-utility-network-unichain-700 ring-utility-network-unichain-200",
    addon: "text-utility-network-unichain-500",
    addonButton: "hover:bg-utility-network-unichain-100 text-utility-network-unichain-400 hover:text-utility-network-unichain-500"
  },
  "network-robinhood": {
    root: "bg-utility-network-robinhood-50 text-utility-network-robinhood-700 ring-utility-network-robinhood-200",
    addon: "text-utility-network-robinhood-500",
    addonButton: "hover:bg-utility-network-robinhood-100 text-utility-network-robinhood-400 hover:text-utility-network-robinhood-500"
  },
  "protocol-uniswap-v3": {
    root: "bg-utility-protocol-uniswap-v3-50 text-utility-protocol-uniswap-v3-700 ring-utility-protocol-uniswap-v3-200",
    addon: "text-utility-protocol-uniswap-v3-500",
    addonButton: "hover:bg-utility-protocol-uniswap-v3-100 text-utility-protocol-uniswap-v3-400 hover:text-utility-protocol-uniswap-v3-500"
  },
  "protocol-uniswap-v4": {
    root: "bg-utility-protocol-uniswap-v4-50 text-utility-protocol-uniswap-v4-700 ring-utility-protocol-uniswap-v4-200",
    addon: "text-utility-protocol-uniswap-v4-500",
    addonButton: "hover:bg-utility-protocol-uniswap-v4-100 text-utility-protocol-uniswap-v4-400 hover:text-utility-protocol-uniswap-v4-500"
  }
};
const addonOnlyColors = Object.fromEntries(Object.entries(filledColors).map(([key, value]) => [key, {
  root: "",
  addon: value.addon
}])) as Record<BadgeColors, {
  root: string;
  addon: string;
}>;
const withPillTypes = {
  [badgeTypes.pillColor]: {
    common: "size-max flex items-center whitespace-nowrap rounded-full ring-1 ring-inset",
    styles: filledColors
  },
  [badgeTypes.badgeColor]: {
    common: "size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset",
    styles: filledColors
  },
  [badgeTypes.badgeModern]: {
    common: "size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset shadow-xs",
    styles: {
      gray: {
        root: "bg-primary text-secondary ring-primary",
        addon: "text-gray-500",
        addonButton: "hover:bg-utility-gray-100 text-utility-gray-400 hover:text-utility-gray-500"
      }
    }
  }
};
const withBadgeTypes = {
  [badgeTypes.pillColor]: {
    common: "size-max flex items-center whitespace-nowrap rounded-full ring-1 ring-inset",
    styles: filledColors
  },
  [badgeTypes.badgeColor]: {
    common: "size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset",
    styles: filledColors
  },
  [badgeTypes.badgeModern]: {
    common: "size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset bg-primary text-secondary ring-primary shadow-xs",
    styles: addonOnlyColors
  }
};
export type BadgeColor<T extends BadgeTypes> = BadgeTypeToColorMap<typeof withPillTypes>[T];
interface BadgeProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  color?: BadgeColor<T>;
  children: ReactNode;
  className?: string;
}
export const Badge = props => {
  const $ = _c(10);
  const {
    type: t0,
    size: t1,
    color: t2,
    children
  } = props;
  const type = t0 === undefined ? "pill-color" : t0;
  const size = t1 === undefined ? "md" : t1;
  const color = t2 === undefined ? "gray" : t2;
  const colors = withPillTypes[type];
  let t3;
  if ($[0] !== color || $[1] !== colors.common || $[2] !== colors.styles || $[3] !== props.className || $[4] !== size || $[5] !== type) {
    const pillSizes = {
      sm: "py-0.5 px-2 text-xs font-medium",
      md: "py-0.5 px-2.5 text-sm font-medium",
      lg: "py-1 px-3 text-sm font-medium",
      xl: "py-1.5 px-4 text-base font-medium"
    };
    const badgeSizes = {
      sm: "py-0.5 px-1.5 text-xs font-medium",
      md: "py-0.5 px-2 text-sm font-medium",
      lg: "py-1 px-2.5 text-sm font-medium rounded-lg",
      xl: "py-1.5 px-3 text-base font-medium rounded-lg"
    };
    const sizes = {
      [badgeTypes.pillColor]: pillSizes,
      [badgeTypes.badgeColor]: badgeSizes,
      [badgeTypes.badgeModern]: badgeSizes
    };
    t3 = cx(colors.common, sizes[type][size], colors.styles[color].root, props.className);
    $[0] = color;
    $[1] = colors.common;
    $[2] = colors.styles;
    $[3] = props.className;
    $[4] = size;
    $[5] = type;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <span className={t3}>{children}</span>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
interface BadgeWithDotProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  color?: BadgeTypeToColorMap<typeof withBadgeTypes>[T];
  className?: string;
  children: ReactNode;
}
export const BadgeWithDot = props => {
  const $ = _c(13);
  const {
    size: t0,
    color: t1,
    type: t2,
    className,
    children
  } = props;
  const size = t0 === undefined ? "md" : t0;
  const color = t1 === undefined ? "gray" : t1;
  const type = t2 === undefined ? "pill-color" : t2;
  const colors = withBadgeTypes[type];
  let t3;
  if ($[0] !== className || $[1] !== color || $[2] !== colors.common || $[3] !== colors.styles || $[4] !== size || $[5] !== type) {
    const pillSizes = {
      sm: "gap-1 py-0.5 pl-1.5 pr-2 text-xs font-medium",
      md: "gap-1.5 py-0.5 pl-2 pr-2.5 text-sm font-medium",
      lg: "gap-1.5 py-1 pl-2.5 pr-3 text-sm font-medium",
      xl: "gap-2 py-1.5 pl-3 pr-4 text-base font-medium"
    };
    const badgeSizes = {
      sm: "gap-1 py-0.5 px-1.5 text-xs font-medium",
      md: "gap-1.5 py-0.5 px-2 text-sm font-medium",
      lg: "gap-1.5 py-1 px-2.5 text-sm font-medium rounded-lg",
      xl: "gap-2 py-1.5 px-3 text-base font-medium rounded-lg"
    };
    const sizes = {
      [badgeTypes.pillColor]: pillSizes,
      [badgeTypes.badgeColor]: badgeSizes,
      [badgeTypes.badgeModern]: badgeSizes
    };
    t3 = cx(colors.common, sizes[type][size], colors.styles[color].root, className);
    $[0] = className;
    $[1] = color;
    $[2] = colors.common;
    $[3] = colors.styles;
    $[4] = size;
    $[5] = type;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = colors.styles[color];
  let t5;
  if ($[7] !== t4.addon) {
    t5 = <Dot className={t4.addon} size="sm" />;
    $[7] = t4.addon;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== children || $[10] !== t3 || $[11] !== t5) {
    t6 = <span className={t3}>{t5}{children}</span>;
    $[9] = children;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
interface BadgeWithIconProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  color?: BadgeTypeToColorMap<typeof withBadgeTypes>[T];
  iconLeading?: IconComponentType;
  iconTrailing?: IconComponentType;
  children: ReactNode;
  className?: string;
}
export const BadgeWithIcon = props => {
  const $ = _c(21);
  const {
    size: t0,
    color: t1,
    type: t2,
    iconLeading: IconLeading,
    iconTrailing: IconTrailing,
    children,
    className
  } = props;
  const size = t0 === undefined ? "md" : t0;
  const color = t1 === undefined ? "gray" : t1;
  const type = t2 === undefined ? "pill-color" : t2;
  const colors = withBadgeTypes[type];
  const icon = IconLeading ? "leading" : "trailing";
  let t3;
  if ($[0] !== className || $[1] !== color || $[2] !== colors.common || $[3] !== colors.styles || $[4] !== icon || $[5] !== size || $[6] !== type) {
    const pillSizes = {
      sm: {
        trailing: "gap-0.5 py-0.5 pl-2 pr-1.5 text-xs font-medium",
        leading: "gap-0.5 py-0.5 pr-2 pl-1.5 text-xs font-medium"
      },
      md: {
        trailing: "gap-1 py-0.5 pl-2.5 pr-2 text-sm font-medium",
        leading: "gap-1 py-0.5 pr-2.5 pl-2 text-sm font-medium"
      },
      lg: {
        trailing: "gap-1 py-1 pl-3 pr-2.5 text-sm font-medium",
        leading: "gap-1 py-1 pr-3 pl-2.5 text-sm font-medium"
      },
      xl: {
        trailing: "gap-1.5 py-1.5 pl-4 pr-3 text-base font-medium",
        leading: "gap-1.5 py-1.5 pr-4 pl-3 text-base font-medium"
      }
    };
    const badgeSizes = {
      sm: {
        trailing: "gap-0.5 py-0.5 pl-2 pr-1.5 text-xs font-medium",
        leading: "gap-0.5 py-0.5 pr-2 pl-1.5 text-xs font-medium"
      },
      md: {
        trailing: "gap-1 py-0.5 pl-2 pr-1.5 text-sm font-medium",
        leading: "gap-1 py-0.5 pr-2 pl-1.5 text-sm font-medium"
      },
      lg: {
        trailing: "gap-1 py-1 pl-2.5 pr-2 text-sm font-medium rounded-lg",
        leading: "gap-1 py-1 pr-2.5 pl-2 text-sm font-medium rounded-lg"
      },
      xl: {
        trailing: "gap-1.5 py-1.5 pl-3 pr-2.5 text-base font-medium rounded-lg",
        leading: "gap-1.5 py-1.5 pr-3 pl-2.5 text-base font-medium rounded-lg"
      }
    };
    const sizes = {
      [badgeTypes.pillColor]: pillSizes,
      [badgeTypes.badgeColor]: badgeSizes,
      [badgeTypes.badgeModern]: badgeSizes
    };
    t3 = cx(colors.common, sizes[type][size][icon], colors.styles[color].root, className);
    $[0] = className;
    $[1] = color;
    $[2] = colors.common;
    $[3] = colors.styles;
    $[4] = icon;
    $[5] = size;
    $[6] = type;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== IconLeading || $[9] !== color || $[10] !== colors.styles) {
    t4 = IconLeading && <IconLeading className={cx(colors.styles[color].addon, "size-3 stroke-3")} />;
    $[8] = IconLeading;
    $[9] = color;
    $[10] = colors.styles;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== IconTrailing || $[13] !== color || $[14] !== colors.styles) {
    t5 = IconTrailing && <IconTrailing className={cx(colors.styles[color].addon, "size-3 stroke-3")} />;
    $[12] = IconTrailing;
    $[13] = color;
    $[14] = colors.styles;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== children || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <span className={t3}>{t4}{children}{t5}</span>;
    $[16] = children;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
};
interface BadgeWithFlagProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  flag?: FlagTypes;
  color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
  children: ReactNode;
}
export const BadgeWithFlag = props => {
  const $ = _c(13);
  const {
    size: t0,
    color: t1,
    flag: t2,
    type: t3,
    children
  } = props;
  const size = t0 === undefined ? "md" : t0;
  const color = t1 === undefined ? "gray" : t1;
  const flag = t2 === undefined ? "AU" : t2;
  const type = t3 === undefined ? "pill-color" : t3;
  const colors = withPillTypes[type];
  let t4;
  if ($[0] !== color || $[1] !== colors.common || $[2] !== colors.styles || $[3] !== size || $[4] !== type) {
    const pillSizes = {
      sm: "gap-1 py-0.5 pl-0.75 pr-2 text-xs font-medium",
      md: "gap-1.5 py-0.5 pl-1 pr-2.5 text-sm font-medium",
      lg: "gap-1.5 py-1 pl-1.5 pr-3 text-sm font-medium",
      xl: "gap-2 py-1.5 pl-2 pr-4 text-base font-medium"
    };
    const badgeSizes = {
      sm: "gap-1 py-0.5 pl-1 pr-1.5 text-xs font-medium",
      md: "gap-1.5 py-0.5 pl-1.5 pr-2 text-sm font-medium",
      lg: "gap-1.5 py-1 pl-2 pr-2.5 text-sm font-medium rounded-lg",
      xl: "gap-2 py-1.5 pl-2.5 pr-3 text-base font-medium rounded-lg"
    };
    const sizes = {
      [badgeTypes.pillColor]: pillSizes,
      [badgeTypes.badgeColor]: badgeSizes,
      [badgeTypes.badgeModern]: badgeSizes
    };
    t4 = cx(colors.common, sizes[type][size], colors.styles[color].root);
    $[0] = color;
    $[1] = colors.common;
    $[2] = colors.styles;
    $[3] = size;
    $[4] = type;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `https://www.untitledui.com/images/flags/${flag}.svg`;
  const t6 = `${flag} flag`;
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = <img src={t5} className="size-4 max-w-none rounded-full" alt={t6} />;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== children || $[10] !== t4 || $[11] !== t7) {
    t8 = <span className={t4}>{t7}{children}</span>;
    $[9] = children;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
};
interface BadgeWithImageProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  imgSrc: string;
  color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
  children: ReactNode;
}
export const BadgeWithImage = props => {
  const $ = _c(13);
  const {
    size: t0,
    color: t1,
    type: t2,
    imgSrc,
    children
  } = props;
  const size = t0 === undefined ? "md" : t0;
  const color = t1 === undefined ? "gray" : t1;
  const type = t2 === undefined ? "pill-color" : t2;
  const colors = withPillTypes[type];
  let t3;
  if ($[0] !== color || $[1] !== colors.common || $[2] !== colors.styles || $[3] !== size || $[4] !== type) {
    const pillSizes = {
      sm: "gap-1 py-0.5 pl-0.75 pr-2 text-xs font-medium",
      md: "gap-1.5 py-0.5 pl-1 pr-2.5 text-sm font-medium",
      lg: "gap-1.5 py-1 pl-1.5 pr-3 text-sm font-medium",
      xl: "gap-2 py-1.5 pl-2 pr-4 text-base font-medium"
    };
    const badgeSizes = {
      sm: "gap-1 py-0.5 pl-1 pr-1.5 text-xs font-medium",
      md: "gap-1.5 py-0.5 pl-1.5 pr-2 text-sm font-medium",
      lg: "gap-1.5 py-1 pl-2 pr-2.5 text-sm font-medium rounded-lg",
      xl: "gap-2 py-1.5 pl-2.5 pr-3 text-base font-medium rounded-lg"
    };
    const sizes = {
      [badgeTypes.pillColor]: pillSizes,
      [badgeTypes.badgeColor]: badgeSizes,
      [badgeTypes.badgeModern]: badgeSizes
    };
    t3 = cx(colors.common, sizes[type][size], colors.styles[color].root);
    $[0] = color;
    $[1] = colors.common;
    $[2] = colors.styles;
    $[3] = size;
    $[4] = type;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cx("size-4 max-w-none rounded-full", IMAGE_OUTLINE_CLASS);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== imgSrc) {
    t5 = <img src={imgSrc} className={t4} alt="Badge image" />;
    $[7] = imgSrc;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== children || $[10] !== t3 || $[11] !== t5) {
    t6 = <span className={t3}>{t5}{children}</span>;
    $[9] = children;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
interface BadgeWithButtonProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  icon?: IconComponentType;
  color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
  children: ReactNode;
  /**
   * The label for the button.
   */
  buttonLabel?: string;
  /**
   * The click event handler for the button.
   */
  onButtonClick?: MouseEventHandler<HTMLButtonElement>;
}
export const BadgeWithButton = props => {
  const $ = _c(20);
  const {
    size: t0,
    color: t1,
    type: t2,
    icon: t3,
    buttonLabel,
    children
  } = props;
  const size = t0 === undefined ? "md" : t0;
  const color = t1 === undefined ? "gray" : t1;
  const type = t2 === undefined ? "pill-color" : t2;
  const Icon = t3 === undefined ? CloseX : t3;
  const colors = withPillTypes[type];
  let t4;
  if ($[0] !== color || $[1] !== colors.common || $[2] !== colors.styles || $[3] !== size || $[4] !== type) {
    const pillSizes = {
      sm: "gap-0.5 py-0.5 pl-2 pr-0.75 text-xs font-medium",
      md: "gap-0.5 py-0.5 pl-2.5 pr-1 text-sm font-medium",
      lg: "gap-0.5 py-1 pl-3 pr-1.5 text-sm font-medium",
      xl: "gap-1 py-1.5 pl-4 pr-2 text-base font-medium"
    };
    const badgeSizes = {
      sm: "gap-0.5 py-0.5 pl-1.5 pr-0.75 text-xs font-medium",
      md: "gap-0.5 py-0.5 pl-2 pr-1 text-sm font-medium",
      lg: "gap-0.5 py-1 pl-2.5 pr-1.5 text-sm font-medium rounded-lg",
      xl: "gap-1 py-1.5 pl-3 pr-2 text-base font-medium rounded-lg"
    };
    const sizes = {
      [badgeTypes.pillColor]: pillSizes,
      [badgeTypes.badgeColor]: badgeSizes,
      [badgeTypes.badgeModern]: badgeSizes
    };
    t4 = cx(colors.common, sizes[type][size], colors.styles[color].root);
    $[0] = color;
    $[1] = colors.common;
    $[2] = colors.styles;
    $[3] = size;
    $[4] = type;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = props.onButtonClick;
  const t6 = colors.styles[color];
  const t7 = type === "pill-color" ? "rounded-full" : "rounded-[3px]";
  let t8;
  if ($[6] !== t6.addonButton || $[7] !== t7) {
    t8 = cx("flex cursor-pointer items-center justify-center p-0.5 outline-focus-ring transition duration-100 ease-linear focus-visible:outline-2", t6.addonButton, t7);
    $[6] = t6.addonButton;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== Icon) {
    t9 = <Icon className="size-3 stroke-[3px] transition-inherit-all" />;
    $[9] = Icon;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== buttonLabel || $[12] !== props.onButtonClick || $[13] !== t8 || $[14] !== t9) {
    t10 = <button type="button" aria-label={buttonLabel} onClick={t5} className={t8}>{t9}</button>;
    $[11] = buttonLabel;
    $[12] = props.onButtonClick;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== children || $[17] !== t10 || $[18] !== t4) {
    t11 = <span className={t4}>{children}{t10}</span>;
    $[16] = children;
    $[17] = t10;
    $[18] = t4;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
};
interface BadgeIconProps<T extends BadgeTypes> {
  type?: T;
  size?: Sizes;
  icon: IconComponentType;
  color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
  children?: ReactNode;
}
export const BadgeIcon = props => {
  const $ = _c(14);
  const {
    size: t0,
    color: t1,
    type: t2,
    icon: Icon
  } = props;
  const size = t0 === undefined ? "md" : t0;
  const color = t1 === undefined ? "gray" : t1;
  const type = t2 === undefined ? "pill-color" : t2;
  const colors = withPillTypes[type];
  let t3;
  if ($[0] !== color || $[1] !== colors.common || $[2] !== colors.styles || $[3] !== size || $[4] !== type) {
    const pillSizes = {
      sm: "p-1.25",
      md: "p-1.5",
      lg: "p-2",
      xl: "p-2.5"
    };
    const badgeSizes = {
      sm: "p-1.25",
      md: "p-1.5",
      lg: "p-2 rounded-lg",
      xl: "p-2.5 rounded-lg"
    };
    const sizes = {
      [badgeTypes.pillColor]: pillSizes,
      [badgeTypes.badgeColor]: badgeSizes,
      [badgeTypes.badgeModern]: badgeSizes
    };
    t3 = cx(colors.common, sizes[type][size], colors.styles[color].root);
    $[0] = color;
    $[1] = colors.common;
    $[2] = colors.styles;
    $[3] = size;
    $[4] = type;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = colors.styles[color];
  let t5;
  if ($[6] !== t4.addon) {
    t5 = cx("size-3 stroke-[3px]", t4.addon);
    $[6] = t4.addon;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== Icon || $[9] !== t5) {
    t6 = <Icon className={t5} />;
    $[8] = Icon;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t6) {
    t7 = <span className={t3}>{t6}</span>;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};